import { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { EpicMoversParamType, TopGainerAndLoserData } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import {
  epicMoversFilterOption,
  setEpicMoversFilterOption,
} from '../../lib/store/discover/epicMoversState';
import { EPIC_MOVERS_PAGE_SIZE } from '../../lib/store/fundamental/constants';
import { getEpicMovers as getEpicMoversCall } from '../../lib/store/fundamental/index';
import {
  epicMoversOptionToString,
  getEpicMoversOptionByStringValue,
} from '../../lib/store/fundamental/mappers';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { defaultLogoBase64 } from '../../lib/util/assets/logos';
import {
  isCallStatusPending,
  isCallStatusReady,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import { useIsMobile, usePriceAndChartLoading, useSubPageTitle } from '../../hooks';
import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import EpicMoversInternal from '../../shared_components/EpicMovers/EpicMoversInternal/EpicMoversInternal';
import {
  EpicMoversInternalRowModel,
} from '../../shared_components/EpicMovers/EpicMoversInternal/EpicMoversInternal.types';
import { UI_ROUTES } from '../../util/routes';

import {
  getActiveTabStocks,
  getInitialTabIndex,
  getIsNoData,
  TOP_GAINERS_TAB_INDEX,
} from './epicMoversInternal.helpers';

const EpicMoversInternalWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getEpicMovers,
    isEpicMoversPending,
    isEpicMoversReady,
    epicMoversData,
    companyLogoAndName,
  } = props;
  const { responseData } = epicMoversData;
  const { topGainers = [], topLosers = [] } = responseData ?? {};

  const { isMobile } = useIsMobile();
  const location = useLocation();
  const titleRef = useSubPageTitle<HTMLDivElement>();
  const navigate = useNavigate();
  const { state } = location;

  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    () => getInitialTabIndex({ isTopGainersSelected: state?.isTopGainersSelected, isMobile }),
  );
  const [visibleCount, setVisibleCount] = useState(EPIC_MOVERS_PAGE_SIZE);

  const activeTabStocks = getActiveTabStocks({ topGainers, topLosers, activeTabIndex });
  const paginatedStocks = useMemo(() => activeTabStocks.slice(0, visibleCount), [activeTabStocks, visibleCount]);

  // Single market: the row's market fields are constants here, not resolved per symbol.
  const rows = useMemo((): EpicMoversInternalRowModel[] => paginatedStocks.map((item: TopGainerAndLoserData) => {
    const entry = companyLogoAndName[item.symbol];

    return {
      ...item,
      countryDesignation: '',
      lookupSymbol: item.symbol,
      currencySymbol: CurrencySymbolEnum.USD,
      isEuropeanSymbol: false,
      logo: entry?.logo ?? defaultLogoBase64,
      url: entry?.url ?? item.url ?? '',
      displayName: item.name ?? entry?.name ?? '',
    };
  }), [paginatedStocks, companyLogoAndName]);

  const isLoading = usePriceAndChartLoading({
    stocks: paginatedStocks,
    isApiPending: isEpicMoversPending,
  });

  const isNoData = getIsNoData({ topGainers, topLosers, activeTabIndex, isPending: isEpicMoversPending });

  const symbolsToSubscribe = useMemo(() => paginatedStocks.map(item => ({
    symbol: item.symbol,
    stockExchangeCode: item.stockExchangeCode,
    countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
  })), [paginatedStocks, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isEpicMoversReady && symbolsToSubscribe.length > 0,
  });

  const optionValue = epicMoversFilterOption.use();

  useEffect(() => {
    getEpicMovers({ range: optionValue });
  }, [getEpicMovers, optionValue]);

  // The fetch effect above reacts to the new option - dispatching here as well would fire twice.
  const handleFilterChange = (_range: number[], valueOption: string) => {
    setEpicMoversFilterOption(getEpicMoversOptionByStringValue(valueOption));
  };

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    setVisibleCount(EPIC_MOVERS_PAGE_SIZE);
    // Update browser history so the tab is preserved when navigating back
    navigate(location.pathname, {
      replace: true,
      state: { isTopGainersSelected: index === TOP_GAINERS_TAB_INDEX },
    });
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleLoadMore = () => setVisibleCount(prev => prev + EPIC_MOVERS_PAGE_SIZE);

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  return (
    <EpicMoversInternal
      rows={rows}
      activeTabIndex={activeTabIndex}
      isPending={isEpicMoversPending}
      isLoading={isLoading}
      isNoData={isNoData}
      hasMoreRows={visibleCount < activeTabStocks.length}
      filterOptionValue={epicMoversOptionToString(optionValue)}
      onTabChange={handleTabChange}
      onFilterChange={handleFilterChange}
      onLoadMore={handleLoadMore}
      onSymbolClick={handleSymbolClick}
      titleRef={titleRef}
      onGoBack={handleGoBack}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isEpicMoversPending: isCallStatusPending(FundamentalCall.getEpicMovers, state.fundamental.statusByCall),
  isEpicMoversReady: isCallStatusReady(FundamentalCall.getEpicMovers, state.fundamental.statusByCall),
  epicMoversData: state.fundamental.epicMoversData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getEpicMovers: (payload: EpicMoversParamType) => dispatch(getEpicMoversCall(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EpicMoversInternalWrapper);
